<template>
    <transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
    >
        <div v-if="show" class="fixed inset-0 z-[150] bg-[#0b141a] flex flex-col font-sans overflow-hidden">
            <!-- Header -->
            <div class="h-16 flex items-center px-6 justify-between text-white/90 border-b border-white/5 bg-[#111b21]">
                <div class="flex items-center space-x-4">
                    <button @click="$emit('cancel')" class="p-2 hover:bg-white/10 rounded-full transition-colors text-white/70 hover:text-white">
                        <XIcon class="h-6 w-6" />
                    </button>
                    <h3 class="font-bold">Edit Photo</h3>
                </div>
                
                <div class="flex items-center space-x-2">
                    <button 
                        @click="undo" 
                        :disabled="!canUndo"
                        class="p-2 hover:bg-white/10 rounded-full transition-colors disabled:opacity-30 text-white/70 hover:text-white"
                        title="Undo"
                    >
                        <ReplyIcon class="h-5 w-5" />
                    </button>
                    <button 
                        @click="save" 
                        class="px-6 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-bold text-sm transition-all active:scale-95 ml-2 shadow-lg shadow-emerald-500/20"
                    >
                        Done
                    </button>
                </div>
            </div>

            <!-- Editor Workspace -->
            <div class="flex-1 relative flex items-center justify-center p-8 overflow-hidden bg-[#0b141a]">
                <div 
                    ref="container" 
                    class="relative max-w-full max-h-full shadow-2xl overflow-hidden"
                    :style="containerStyle"
                >
                    <!-- Base Canvas (Image + Previous Edits) -->
                    <canvas 
                        ref="mainCanvas" 
                        class="block"
                        :style="canvasTransformStyle"
                    ></canvas>
                    
                    <!-- Drawing Layer -->
                    <canvas 
                        ref="drawCanvas" 
                        class="absolute cursor-crosshair transition-opacity duration-200"
                        :class="mode === 'crop' ? 'opacity-0 pointer-events-none' : 'opacity-100'"
                        :style="canvasTransformStyle"
                        @mousedown="startDrawing"
                        @mousemove="draw"
                        @mouseup="stopDrawing"
                        @mouseleave="stopDrawing"
                        @touchstart.prevent="startDrawing"
                        @touchmove.prevent="draw"
                        @touchend.prevent="stopDrawing"
                    ></canvas>

                    <!-- Cropping Overlay -->
                    <div 
                        v-if="mode === 'crop'" 
                        class="absolute inset-0 bg-black/60 z-10"
                    >
                        <div 
                            ref="cropBox"
                            class="absolute border-2 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] cursor-move"
                            :style="cropBoxStyle"
                            @mousedown="startCropMove"
                        >
                            <!-- Resize Handles -->
                            <div class="absolute -top-1 -left-1 w-4 h-4 bg-white rounded-sm cursor-nw-resize"></div>
                            <div class="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-sm cursor-ne-resize"></div>
                            <div class="absolute -bottom-1 -left-1 w-4 h-4 bg-white rounded-sm cursor-sw-resize"></div>
                            <div class="absolute -bottom-1 -right-1 w-4 h-4 bg-white rounded-sm cursor-se-resize"></div>
                            
                            <!-- Grid Lines -->
                            <div class="absolute inset-0 grid grid-cols-3 grid-rows-3 opacity-30 pointer-events-none">
                                <div class="border-r border-white"></div>
                                <div class="border-r border-white"></div>
                                <div></div>
                                <div class="border-t border-white col-span-3"></div>
                                <div class="border-t border-white col-span-3"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Toolbar -->
            <div class="h-20 bg-[#111b21] border-t border-white/5 flex items-center justify-center px-6 space-x-1">
                <button 
                    @click="setMode('draw')" 
                    class="flex flex-col items-center justify-center w-16 h-16 rounded-xl transition-all"
                    :class="mode === 'draw' ? 'bg-white/10 text-emerald-500' : 'text-white/40 hover:text-white/70'"
                >
                    <PencilIcon class="h-6 w-6" />
                    <span class="text-[10px] font-bold mt-1 uppercase tracking-tighter">Draw</span>
                </button>
                
                <button 
                    @click="setMode('crop')" 
                    class="flex flex-col items-center justify-center w-16 h-16 rounded-xl transition-all"
                    :class="mode === 'crop' ? 'bg-white/10 text-emerald-500' : 'text-white/40 hover:text-white/70'"
                >
                    <CropIcon class="h-6 w-6" />
                    <span class="text-[10px] font-bold mt-1 uppercase tracking-tighter">Crop</span>
                </button>

                <div class="w-px h-8 bg-white/5 mx-4"></div>

                <!-- Color Palette (Only in Draw Mode) -->
                <div v-if="mode === 'draw'" class="flex items-center space-x-2 animate-in slide-in-from-bottom-2 duration-300 relative">
                    <button 
                        v-for="color in colors" 
                        :key="color"
                        @click="currentColor = color"
                        class="h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 active:scale-90"
                        :style="{ backgroundColor: color }"
                        :class="currentColor === color ? 'border-white scale-110 shadow-lg' : 'border-transparent opacity-60'"
                    ></button>

                    <!-- Custom Color Picker Trigger -->
                    <button 
                        @click="showColorPicker = !showColorPicker"
                        class="h-8 w-8 rounded-full border-2 border-white/20 flex items-center justify-center transition-all hover:border-white group overflow-hidden bg-gradient-to-tr from-red-500 via-green-500 to-blue-500"
                        :class="showColorPicker ? 'ring-2 ring-emerald-500' : ''"
                    >
                        <ColorSwatchIcon class="h-4 w-4 text-white group-hover:scale-110" />
                    </button>

                    <!-- Custom Color Picker Popover -->
                    <div v-if="showColorPicker" class="absolute bottom-16 left-0 w-64 bg-[#111b21] rounded-2xl shadow-2xl border border-white/10 overflow-hidden z-50 animate-in slide-in-from-bottom-4 duration-300">
                        <div 
                            ref="spectrum"
                            class="h-32 w-full relative cursor-crosshair"
                            style="background: linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)"
                            @mousedown="startColorPick"
                        >
                            <div class="absolute inset-0 bg-gradient-to-b from-white via-transparent to-black opacity-50"></div>
                            <div 
                                class="absolute h-4 w-4 border-2 border-white rounded-md shadow-lg pointer-events-none -translate-x-1/2 -translate-y-1/2"
                                :style="{ left: pickerPos.x + 'px', top: pickerPos.y + 'px' }"
                            ></div>
                        </div>
                        <div class="p-4 flex items-center justify-between">
                            <div class="flex items-center space-x-3">
                                <div class="h-10 w-10 rounded-full border border-white/10 shadow-inner" :style="{ backgroundColor: currentColor }"></div>
                                <span class="text-sm font-mono text-white/90 uppercase">{{ currentColor }}</span>
                            </div>
                            <button @click="showColorPicker = false" class="text-xs font-bold text-emerald-500 hover:text-emerald-400">Apply</button>
                        </div>
                    </div>
                </div>

                <!-- Crop Actions -->
                <div v-if="mode === 'crop'" class="flex items-center space-x-6 animate-in slide-in-from-bottom-2 duration-300">
                    <button @click="rotate(-90)" class="p-2 hover:bg-white/10 rounded-full text-white/70 hover:text-white transition-colors" title="Rotate Left">
                        <RotateLeftIcon class="h-6 w-6" />
                    </button>
                    <button @click="rotate(90)" class="p-2 hover:bg-white/10 rounded-full text-white/70 hover:text-white transition-colors" title="Rotate Right">
                        <RotateRightIcon class="h-6 w-6" />
                    </button>
                    <button @click="resetCrop" class="text-xs font-black uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors ml-4">Reset</button>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import { 
    XIcon, 
    PencilIcon, 
    ReplyIcon,
    PhotographIcon,
    ColorSwatchIcon
} from "@heroicons/vue/outline";

// Custom Icons
const CropIcon = {
    template: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6.13 1L6 16a2 2 0 0 0 2 2h15"/><path d="M1 6.13L16 6a2 2 0 0 1 2 2v15"/></svg>'
};

const RotateLeftIcon = {
    template: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 2v6h6M2.66 15.57a10 10 0 1 0 .57-8.38"/></svg>'
};

const RotateRightIcon = {
    template: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38"/></svg>'
};

export default {
    name: 'ImageEditor',
    components: {
        XIcon,
        PencilIcon,
        ReplyIcon,
        PhotographIcon,
        CropIcon,
        RotateLeftIcon,
        RotateRightIcon
    },
    props: {
        show: Boolean,
        imageFile: File
    },
    emits: ['cancel', 'save'],
    data() {
        return {
            mode: 'draw', // draw, crop
            currentColor: '#00a884',
            colors: ['#ffffff', '#00a884', '#007bff', '#ff3b30', '#ffcc00', '#af52de'],
            isDrawing: false,
            history: [],
            img: null,
            ctx: null,
            drawCtx: null,
            canvasWidth: 0,
            canvasHeight: 0,
            cropBox: {
                x: 0,
                y: 0,
                width: 100,
                height: 100
            },
            showColorPicker: false,
            pickerPos: { x: 50, y: 50 }
        };
    },
    computed: {
        containerStyle() {
            if (this.mode === 'crop') {
                return {
                    width: this.canvasWidth + 'px',
                    height: this.canvasHeight + 'px',
                };
            } else {
                // In Draw mode, shrink the container to the crop box
                return {
                    width: (this.canvasWidth * this.cropBox.width / 100) + 'px',
                    height: (this.canvasHeight * this.cropBox.height / 100) + 'px',
                };
            }
        },
        canvasTransformStyle() {
            if (this.mode === 'crop') {
                return {
                    width: this.canvasWidth + 'px',
                    height: this.canvasHeight + 'px',
                    top: 0,
                    left: 0
                };
            } else {
                // In Draw mode, offset the canvas to show the cropped area
                return {
                    width: this.canvasWidth + 'px',
                    height: this.canvasHeight + 'px',
                    position: 'absolute',
                    top: (-this.canvasHeight * this.cropBox.y / 100) + 'px',
                    left: (-this.canvasWidth * this.cropBox.x / 100) + 'px',
                };
            }
        },
        cropBoxStyle() {
            return {
                left: this.cropBox.x + '%',
                top: this.cropBox.y + '%',
                width: this.cropBox.width + '%',
                height: this.cropBox.height + '%'
            };
        },
        canUndo() {
            return this.history.length > 0;
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.initEditor();
            }
        }
    },
    mounted() {
        window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleResize() {
            if (this.show && this.img) {
                this.setupCanvasesFromCurrent();
            }
        },
        async initEditor() {
            if (!this.imageFile) return;
            
            const reader = new FileReader();
            reader.onload = (e) => {
                this.img = new Image();
                this.img.onload = () => {
                    this.setupCanvases();
                    this.history = [];
                    this.setMode('draw');
                };
                this.img.src = e.target.result;
            };
            reader.readAsDataURL(this.imageFile);
        },
        setupCanvases() {
            const mainCanvas = this.$refs.mainCanvas;
            const drawCanvas = this.$refs.drawCanvas;
            this.ctx = mainCanvas.getContext('2d');
            this.drawCtx = drawCanvas.getContext('2d');

            // Set internal canvas dimensions to image size
            mainCanvas.width = this.img.naturalWidth;
            mainCanvas.height = this.img.naturalHeight;
            drawCanvas.width = this.img.naturalWidth;
            drawCanvas.height = this.img.naturalHeight;

            // Determine display dimensions (preserving aspect ratio)
            const maxWidth = window.innerWidth * 0.8;
            const maxHeight = window.innerHeight * 0.6;
            let width = this.img.naturalWidth;
            let height = this.img.naturalHeight;

            if (width > maxWidth) {
                height *= maxWidth / width;
                width = maxWidth;
            }
            if (height > maxHeight) {
                width *= maxHeight / height;
                height = maxHeight;
            }

            this.canvasWidth = width;
            this.canvasHeight = height;

            // Initial Draw
            this.ctx.drawImage(this.img, 0, 0);
        },
        setMode(mode) {
            if (this.mode === 'draw' && this.isDirtyDrawCanvas()) {
                this.commitDrawing();
            }
            this.mode = mode;
        },
        isDirtyDrawCanvas() {
            // Check if anything was drawn on the drawCanvas
            return true; // Simplified for now
        },
        commitDrawing() {
            const mainCanvas = this.$refs.mainCanvas;
            const drawCanvas = this.$refs.drawCanvas;
            
            // Save state for undo
            this.history.push(mainCanvas.toDataURL());
            
            // Merge drawCanvas into mainCanvas
            this.ctx.drawImage(drawCanvas, 0, 0);
            
            // Clear drawCanvas
            this.drawCtx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
        },
        startDrawing(e) {
            if (this.mode !== 'draw') return;
            this.isDrawing = true;
            this.drawCtx.beginPath();
            const { x, y } = this.getCoordinates(e);
            this.drawCtx.moveTo(x, y);
        },
        draw(e) {
            if (!this.isDrawing || this.mode !== 'draw') return;
            const { x, y } = this.getCoordinates(e);
            
            this.drawCtx.lineTo(x, y);
            this.drawCtx.strokeStyle = this.currentColor;
            this.drawCtx.lineWidth = 5 * (this.img.naturalWidth / this.canvasWidth); // Dynamic width based on scale
            this.drawCtx.lineCap = 'round';
            this.drawCtx.lineJoin = 'round';
            this.drawCtx.stroke();
        },
        stopDrawing() {
            if (this.isDrawing) {
                this.isDrawing = false;
                this.drawCtx.closePath();
                this.commitDrawing();
            }
        },
        getCoordinates(e) {
            const canvas = this.$refs.drawCanvas;
            const rect = canvas.getBoundingClientRect();
            
            let clientX, clientY;
            if (e.touches && e.touches.length > 0) {
                clientX = e.touches[0].clientX;
                clientY = e.touches[0].clientY;
            } else {
                clientX = e.clientX;
                clientY = e.clientY;
            }
            
            // Calculate relative position within the element (0 to 1)
            const relativeX = (clientX - rect.left) / rect.width;
            const relativeY = (clientY - rect.top) / rect.height;
            
            // Map to natural canvas resolution
            return {
                x: relativeX * canvas.width,
                y: relativeY * canvas.height
            };
        },
        undo() {
            if (this.history.length === 0) return;
            
            const prevState = this.history.pop();
            const tempImg = new Image();
            tempImg.onload = () => {
                this.ctx.clearRect(0, 0, this.$refs.mainCanvas.width, this.$refs.mainCanvas.height);
                this.ctx.drawImage(tempImg, 0, 0);
            };
            tempImg.src = prevState;
        },
        startCropMove(e) {
            if (this.mode !== 'crop') return;
            e.preventDefault();
            
            const rect = this.$refs.cropBox.getBoundingClientRect();
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            
            // Check if we clicked a handle
            const handleSize = 20;
            const isNW = clientX < rect.left + handleSize && clientY < rect.top + handleSize;
            const isNE = clientX > rect.right - handleSize && clientY < rect.top + handleSize;
            const isSW = clientX < rect.left + handleSize && clientY > rect.bottom - handleSize;
            const isSE = clientX > rect.right - handleSize && clientY > rect.bottom - handleSize;
            
            this.dragType = isNW ? 'nw' : isNE ? 'ne' : isSW ? 'sw' : isSE ? 'se' : 'move';
            this.startPos = { x: clientX, y: clientY };
            this.startBox = { ...this.cropBox };
            
            window.addEventListener('mousemove', this.onCropMove);
            window.addEventListener('mouseup', this.stopCropMove);
            window.addEventListener('touchmove', this.onCropMove);
            window.addEventListener('touchend', this.stopCropMove);
        },
        onCropMove(e) {
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            
            const dx = ((clientX - this.startPos.x) / this.canvasWidth) * 100;
            const dy = ((clientY - this.startPos.y) / this.canvasHeight) * 100;
            
            let { x, y, width, height } = this.startBox;
            
            if (this.dragType === 'move') {
                x = Math.max(0, Math.min(100 - width, x + dx));
                y = Math.max(0, Math.min(100 - height, y + dy));
            } else if (this.dragType === 'nw') {
                const newX = Math.max(0, Math.min(x + width - 10, x + dx));
                width = width + (x - newX);
                x = newX;
                const newY = Math.max(0, Math.min(y + height - 10, y + dy));
                height = height + (y - newY);
                y = newY;
            } else if (this.dragType === 'ne') {
                width = Math.max(10, Math.min(100 - x, width + dx));
                const newY = Math.max(0, Math.min(y + height - 10, y + dy));
                height = height + (y - newY);
                y = newY;
            } else if (this.dragType === 'sw') {
                const newX = Math.max(0, Math.min(x + width - 10, x + dx));
                width = width + (x - newX);
                x = newX;
                height = Math.max(10, Math.min(100 - y, height + dy));
            } else if (this.dragType === 'se') {
                width = Math.max(10, Math.min(100 - x, width + dx));
                height = Math.max(10, Math.min(100 - y, height + dy));
            }
            
            this.cropBox = { x, y, width, height };
        },
        stopCropMove() {
            window.removeEventListener('mousemove', this.onCropMove);
            window.removeEventListener('mouseup', this.stopCropMove);
            window.removeEventListener('touchmove', this.onCropMove);
            window.removeEventListener('touchend', this.stopCropMove);
        },
        applyCrop() {
            const mainCanvas = this.$refs.mainCanvas;
            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');
            
            const cropX = (this.cropBox.x / 100) * mainCanvas.width;
            const cropY = (this.cropBox.y / 100) * mainCanvas.height;
            const cropW = (this.cropBox.width / 100) * mainCanvas.width;
            const cropH = (this.cropBox.height / 100) * mainCanvas.height;
            
            tempCanvas.width = cropW;
            tempCanvas.height = cropH;
            tempCtx.drawImage(mainCanvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
            
            // Save state for undo
            this.history.push(mainCanvas.toDataURL());
            
            // Resize main canvas
            mainCanvas.width = cropW;
            mainCanvas.height = cropH;
            this.$refs.drawCanvas.width = cropW;
            this.$refs.drawCanvas.height = cropH;
            
            this.ctx.drawImage(tempCanvas, 0, 0);
            
            // Update display dimensions
            this.setupCanvasesFromCurrent();
            this.resetCrop();
        },
        setupCanvasesFromCurrent() {
            const mainCanvas = this.$refs.mainCanvas;
            const maxWidth = window.innerWidth * 0.8;
            const maxHeight = window.innerHeight * 0.6;
            let width = mainCanvas.width;
            let height = mainCanvas.height;

            if (width > maxWidth) {
                height *= maxWidth / width;
                width = maxWidth;
            }
            if (height > maxHeight) {
                width *= maxHeight / height;
                height = maxHeight;
            }

            this.canvasWidth = width;
            this.canvasHeight = height;
        },
        resetCrop() {
            this.cropBox = { x: 0, y: 0, width: 100, height: 100 };
        },
        rotate(degrees) {
            const mainCanvas = this.$refs.mainCanvas;
            const drawCanvas = this.$refs.drawCanvas;
            
            // Save state
            this.history.push(mainCanvas.toDataURL());
            
            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');
            
            // Swap width/height for 90/270 deg
            if (Math.abs(degrees) === 90 || Math.abs(degrees) === 270) {
                tempCanvas.width = mainCanvas.height;
                tempCanvas.height = mainCanvas.width;
            } else {
                tempCanvas.width = mainCanvas.width;
                tempCanvas.height = mainCanvas.height;
            }
            
            tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
            tempCtx.rotate(degrees * Math.PI / 180);
            tempCtx.drawImage(mainCanvas, -mainCanvas.width / 2, -mainCanvas.height / 2);
            
            // Transform Crop Box (only if 90 or -90)
            if (degrees === 90) {
                const { x, y, width, height } = this.cropBox;
                this.cropBox = {
                    x: 100 - (y + height),
                    y: x,
                    width: height,
                    height: width
                };
            } else if (degrees === -90) {
                const { x, y, width, height } = this.cropBox;
                this.cropBox = {
                    x: y,
                    y: 100 - (x + width),
                    width: height,
                    height: width
                };
            }
            
            // Update canvases
            mainCanvas.width = tempCanvas.width;
            mainCanvas.height = tempCanvas.height;
            drawCanvas.width = tempCanvas.width;
            drawCanvas.height = tempCanvas.height;
            
            this.ctx.drawImage(tempCanvas, 0, 0);
            this.setupCanvasesFromCurrent();
        },
        startColorPick(e) {
            this.pickColor(e);
            window.addEventListener('mousemove', this.pickColor);
            window.addEventListener('mouseup', this.stopColorPick);
        },
        pickColor(e) {
            const rect = this.$refs.spectrum.getBoundingClientRect();
            const x = Math.max(0, Math.min(rect.width, (e.clientX || e.touches?.[0].clientX) - rect.left));
            const y = Math.max(0, Math.min(rect.height, (e.clientY || e.touches?.[0].clientY) - rect.top));
            
            this.pickerPos = { x, y };
            
            // Sample color from a hidden canvas for accuracy
            const canvas = document.createElement('canvas');
            canvas.width = rect.width;
            canvas.height = rect.height;
            const ctx = canvas.getContext('2d');
            
            // Redraw spectrum gradient
            const gradH = ctx.createLinearGradient(0, 0, rect.width, 0);
            gradH.addColorStop(0, '#f00');
            gradH.addColorStop(0.17, '#ff0');
            gradH.addColorStop(0.33, '#0f0');
            gradH.addColorStop(0.5, '#0ff');
            gradH.addColorStop(0.67, '#00f');
            gradH.addColorStop(0.83, '#f0f');
            gradH.addColorStop(1, '#f00');
            ctx.fillStyle = gradH;
            ctx.fillRect(0, 0, rect.width, rect.height);
            
            // Redraw V/S gradient
            const gradV = ctx.createLinearGradient(0, 0, 0, rect.height);
            gradV.addColorStop(0, 'rgba(255,255,255,0.5)');
            gradV.addColorStop(0.5, 'rgba(255,255,255,0)');
            gradV.addColorStop(1, 'rgba(0,0,0,0.5)');
            ctx.fillStyle = gradV;
            ctx.fillRect(0, 0, rect.width, rect.height);
            
            const pixel = ctx.getImageData(Math.floor(x), Math.floor(y), 1, 1).data;
            this.currentColor = `#${((1 << 24) + (pixel[0] << 16) + (pixel[1] << 8) + pixel[2]).toString(16).slice(1)}`;
        },
        stopColorPick() {
            window.removeEventListener('mousemove', this.pickColor);
            window.removeEventListener('mouseup', this.stopColorPick);
        },
        save() {
            // Auto-apply current crop if not full
            const isFull = Math.abs(this.cropBox.x) < 0.1 && 
                           Math.abs(this.cropBox.y) < 0.1 && 
                           Math.abs(this.cropBox.width - 100) < 0.1 && 
                           Math.abs(this.cropBox.height - 100) < 0.1;
            
            if (!isFull) {
                this.applyCrop();
            }

            const canvas = this.$refs.mainCanvas;
            canvas.toBlob((blob) => {
                const editedFile = new File([blob], this.imageFile.name, { type: 'image/jpeg' });
                this.$emit('save', editedFile);
            }, 'image/jpeg', 0.9);
        }
    }
}
</script>

<style scoped>
.cursor-nw-resize { cursor: nw-resize; }
.cursor-ne-resize { cursor: ne-resize; }
.cursor-sw-resize { cursor: sw-resize; }
.cursor-se-resize { cursor: se-resize; }

/* Custom Scrollbar for dark theme */
::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}
::-webkit-scrollbar-track {
    background: transparent;
}
::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.2);
}
</style>
